import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectSportsClubId } from '../store/userSlice';
import { useAuth } from '../context/AuthContext';
import { SubscriptionControllerService, PaymentControllerService, InvoiceRecipientDataControllerService } from '../api/index.ts';
import { toast } from 'react-toastify';
import './Payments.css';

function Payments({ embedded }) {
  const { hasRole } = useAuth();
  const isSportsClub = hasRole('SPORTS_CLUB');
  const sportsClubId = useSelector(selectSportsClubId);

  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSeasons, setSelectedSeasons] = useState({});
  const [isPaying, setIsPaying] = useState(false);

  // Invoice recipient data state
  const [invoiceData, setInvoiceData] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ name: '', street: '', postalCode: '', city: '' });
  const [isSavingInvoice, setIsSavingInvoice] = useState(false);
  const [invoiceFormErrors, setInvoiceFormErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      if (!sportsClubId) {
        setError('Nie znaleziono ID klubu');
        setIsLoading(false);
        return;
      }
      try {
        const [subData, invData] = await Promise.allSettled([
          SubscriptionControllerService.getSubscriptionsBySportsClubId(sportsClubId),
          InvoiceRecipientDataControllerService.get(sportsClubId),
        ]);

        if (subData.status === 'fulfilled') {
          setSubscriptionData(subData.value);
          const initial = {};
          (subData.value.currentSubscriptions || []).forEach(team => {
            initial[team.teamId] = new Set();
          });
          setSelectedSeasons(initial);
        } else {
          toast.error('Nie udało się pobrać danych o płatności');
          setError('Nie udało się pobrać danych o płatności');
        }

        if (invData.status === 'fulfilled' && invData.value) {
          setInvoiceData(invData.value);
          setInvoiceForm({
            name: invData.value.name || '',
            street: invData.value.street || '',
            postalCode: invData.value.postalCode || '',
            city: invData.value.city || '',
          });
        }
      } catch (err) {
        toast.error('Nie udało się pobrać danych');
        setError('Nie udało się pobrać danych');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [sportsClubId]);

  const validateInvoiceForm = () => {
    const errors = {};
    if (!invoiceForm.name.trim()) errors.name = 'Nazwa jest wymagana';
    if (!invoiceForm.street.trim()) errors.street = 'Ulica jest wymagana';
    if (!invoiceForm.postalCode.trim()) {
      errors.postalCode = 'Kod pocztowy jest wymagany';
    } else if (!/^\d{2}-\d{3}$/.test(invoiceForm.postalCode.trim())) {
      errors.postalCode = 'Format: XX-XXX';
    }
    if (!invoiceForm.city.trim()) errors.city = 'Miejscowość jest wymagana';
    return errors;
  };

  const handleSaveInvoiceData = async () => {
    const errors = validateInvoiceForm();
    setInvoiceFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSavingInvoice(true);
    try {
      const saved = await InvoiceRecipientDataControllerService.save(sportsClubId, {
        name: invoiceForm.name.trim(),
        street: invoiceForm.street.trim(),
        postalCode: invoiceForm.postalCode.trim(),
        city: invoiceForm.city.trim(),
      });
      setInvoiceData(saved);
      setIsInvoiceModalOpen(false);
      toast.success('Dane do faktury zostały zapisane');
    } catch (err) {
      toast.error('Nie udało się zapisać danych do faktury');
    } finally {
      setIsSavingInvoice(false);
    }
  };

  const openInvoiceModal = () => {
    if (invoiceData) {
      setInvoiceForm({
        name: invoiceData.name || '',
        street: invoiceData.street || '',
        postalCode: invoiceData.postalCode || '',
        city: invoiceData.city || '',
      });
    }
    setInvoiceFormErrors({});
    setIsInvoiceModalOpen(true);
  };

  if (!isSportsClub) {
    return (
      <div className="payments-container">
        <div className="payments-card">
          {!embedded && <h2 className="page-title">Płatności</h2>}
          <div className="payments-content">
            <p className="error-message">Brak dostępu do tej strony.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="payments-container">
        <div className="payments-card">
          {!embedded && <h2 className="page-title">Płatności</h2>}
          <div className="payments-content">
            <div className="loading-message">Ładowanie danych...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payments-container">
        <div className="payments-card">
          {!embedded && <h2 className="page-title">Płatności</h2>}
          <div className="payments-content">
            <p className="error-message">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const teams = subscriptionData?.currentSubscriptions || [];
  const availableSeasons = subscriptionData?.availableSeasonDefinitions || [];

  const seniorTeams = teams.filter(t => t.teamType === 'SENIOR');
  const youthTeams = teams.filter(t => t.teamType === 'YOUTH');

  const handleSeasonToggle = (teamId, seasonId) => {
    setSelectedSeasons(prev => {
      const current = new Set(prev[teamId] || []);
      if (current.has(seasonId)) {
        current.delete(seasonId);
      } else {
        if (current.size >= 2) return prev;
        current.add(seasonId);
      }
      return { ...prev, [teamId]: current };
    });
  };

  const getTeamPrice = (teamId) => {
    const count = selectedSeasons[teamId]?.size || 0;
    if (count === 0) return 0;
    if (count === 1) return 50;
    if (count === 2) return 90;
    return 0;
  };

  const getTeamEndDate = (teamId) => {
    const seasonIds = selectedSeasons[teamId];
    if (!seasonIds || seasonIds.size === 0) return null;

    let maxEndDate = null;
    for (const seasonId of seasonIds) {
      const season = availableSeasons.find(s => s.id === seasonId);
      if (season && season.maxEndDate) {
        const endDate = new Date(season.maxEndDate);
        if (!maxEndDate || endDate > maxEndDate) {
          maxEndDate = endDate;
        }
      }
    }
    if (maxEndDate) {
      maxEndDate.setDate(maxEndDate.getDate() + 7);
    }
    return maxEndDate;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('pl-PL', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const calculateTotal = () => {
    return teams.reduce((sum, team) => sum + getTeamPrice(team.teamId), 0);
  };

  const isSeasonAlreadyActive = (team, seasonId) => {
    return team.seasonDefinitionIds && team.seasonDefinitionIds.includes(seasonId);
  };

  const handlePay = async () => {
    if (!invoiceData) {
      toast.error('Aby dokonać płatności, najpierw podaj dane do faktury');
      return;
    }

    setIsPaying(true);
    try {
      const subscriptions = teams
        .filter(team => selectedSeasons[team.teamId]?.size > 0)
        .map(team => ({
          teamId: team.teamId,
          seasonDefinitionIds: Array.from(selectedSeasons[team.teamId]),
        }));

      if (subscriptions.length === 0) {
        toast.error('Wybierz przynajmniej jeden okres dla drużyny');
        return;
      }

      const response = await PaymentControllerService.initiatePayment({
        sportsClubId: sportsClubId,
        subscriptions: subscriptions,
      });

      if (response.successUrl) {
        const ALLOWED_PAYMENT_HOSTS = ['sparingownia.pl', 'www.sparingownia.pl', 'secure.przelewy24.pl', 'sandbox.przelewy24.pl', 'secure.tpay.com', 'sandbox.tpay.com'];
        try {
          const paymentUrl = new URL(response.successUrl);
          if (!ALLOWED_PAYMENT_HOSTS.includes(paymentUrl.hostname) || paymentUrl.protocol !== 'https:') {
            toast.error('Nieprawidłowy adres płatności');
            return;
          }
        } catch {
          toast.error('Nieprawidłowy adres płatności');
          return;
        }
        const isPWA = window.matchMedia('(display-mode: standalone)').matches
          || window.navigator.standalone === true;
        if (isPWA) {
          window.open(response.successUrl, '_blank');
        } else {
          window.location.href = response.successUrl;
        }
      } else {
        toast.error('Nie otrzymano linku do płatności');
      }
    } catch (err) {
      toast.error('Nie udało się zainicjować płatności');
    } finally {
      setIsPaying(false);
    }
  };

  const renderTeamRow = (team) => {
    const currentSubName = team.subscriptionName || 'Darmowy';
    const isCurrentlyPaid = currentSubName !== 'Darmowy';
    const teamPrice = getTeamPrice(team.teamId);
    const teamEndDate = getTeamEndDate(team.teamId);

    return (
      <div key={team.teamId} className="team-row">
        <div className="team-row-header">
          <span className="team-row-name">{team.teamName}</span>
          <span className="current-plan-badge">
            {isCurrentlyPaid
              ? `Aktualny plan: ${currentSubName} (do ${team.endDate || '?'})`
              : 'Aktualny plan: Darmowy'}
          </span>
        </div>
        <div className="season-options">
          {availableSeasons.map(season => {
            const isAlreadyActive = isSeasonAlreadyActive(team, season.id);
            const isSelected = selectedSeasons[team.teamId]?.has(season.id) || false;
            const canSelect = isSelected || (selectedSeasons[team.teamId]?.size || 0) < 2;

            return (
              <label
                key={season.id}
                className={
                  'season-option' +
                  (isSelected ? ' season-option-selected' : '') +
                  (isAlreadyActive ? ' season-option-active' : '') +
                  (!canSelect && !isAlreadyActive ? ' season-option-disabled' : '')
                }
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={isAlreadyActive || (!canSelect && !isSelected)}
                  onChange={() => handleSeasonToggle(team.teamId, season.id)}
                  className="season-checkbox"
                />
                <span className="season-option-content">
                  <span className="season-option-name">{season.name}</span>
                  <span className="season-option-dates">
                    {season.minStartDate} - {season.maxEndDate}
                  </span>
                  {isAlreadyActive && <span className="season-option-tag">Opłacony</span>}
                </span>
              </label>
            );
          })}
        </div>
        {teamPrice > 0 && (
          <div className="team-row-upgrade-cost">
            Cena: <strong>{teamPrice.toFixed(2)} zł</strong>
            {teamEndDate && (
              <span className="team-end-date"> | Subskrypcja do: {formatDate(teamEndDate)}</span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSection = (title, teamsList) => {
    if (teamsList.length === 0) return null;
    return (
      <div className="team-section">
        <h3 className="team-section-title">{title}</h3>
        <div className="teams-list">
          {teamsList.map(renderTeamRow)}
        </div>
      </div>
    );
  };

  const total = calculateTotal();
  const hasInvoiceData = !!invoiceData;

  return (
    <div className="payments-container">
      <div className="payments-card">
        {!embedded && <h2 className="page-title">Płatności</h2>}
        <div className="payments-content">

          {/* Invoice + pricing side by side */}
          <div className="invoice-pricing-row">
            <div className="invoice-data-section">
              {hasInvoiceData ? (
                <div className="invoice-data-summary">
                  <span className="invoice-data-title">Dane do faktury</span>
                  <div className="invoice-data-details">
                    <span>{invoiceData.name}</span>
                    <span>{invoiceData.street}</span>
                    <span>{invoiceData.postalCode} {invoiceData.city}</span>
                  </div>
                  <button className="btn-invoice-edit" onClick={openInvoiceModal}>Edytuj</button>
                </div>
              ) : (
                <div className="invoice-data-missing">
                  <p className="invoice-data-warning">Aby dokonać płatności, musisz najpierw podać dane do faktury.</p>
                  <button className="btn-invoice" onClick={openInvoiceModal}>Podaj dane do faktury</button>
                </div>
              )}
            </div>

            {teams.length > 0 && availableSeasons.length > 0 && (
              <div className="pricing-info">
                <div className="pricing-info-title">Cennik</div>
                <div className="pricing-info-item">1 okres: <strong>50.00 zł</strong> / drużyna</div>
                <div className="pricing-info-item">2 okresy: <strong>90.00 zł</strong> / drużyna</div>
              </div>
            )}
          </div>

          {teams.length === 0 ? (
            <p className="no-teams-message">Brak drużyn do wyświetlenia.</p>
          ) : availableSeasons.length === 0 ? (
            <p className="no-teams-message">Brak dostępnych okresów sezonowych do wykupienia.</p>
          ) : (
            <>

              <div className="teams-sections-wrapper">
                {renderSection('Seniorzy', seniorTeams)}
              </div>

              <div className="payments-summary">
                <div className="summary-label">
                  Łączna kwota do zapłaty:
                </div>
                <div className="summary-amount">{total.toFixed(2)} zł</div>
              </div>
              <div className="payments-action">
                <button className="btn-pay" onClick={handlePay} disabled={isPaying || total === 0 || !hasInvoiceData}>
                  {isPaying ? 'Przetwarzanie...' : !hasInvoiceData ? 'Uzupełnij dane do faktury' : 'Zapłać'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Invoice data modal */}
      {isInvoiceModalOpen && (
        <div className="modal-overlay" onClick={() => setIsInvoiceModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Dane do faktury</h3>
            <div className="modal-form">
              <div className="form-group">
                <label className="form-label">Nazwa</label>
                <input
                  type="text"
                  className={'form-input' + (invoiceFormErrors.name ? ' form-input-error' : '')}
                  value={invoiceForm.name}
                  onChange={e => setInvoiceForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nazwa firmy / klubu"
                />
                {invoiceFormErrors.name && <span className="form-error">{invoiceFormErrors.name}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Ulica</label>
                <input
                  type="text"
                  className={'form-input' + (invoiceFormErrors.street ? ' form-input-error' : '')}
                  value={invoiceForm.street}
                  onChange={e => setInvoiceForm(prev => ({ ...prev, street: e.target.value }))}
                  placeholder="np. ul. Sportowa 1"
                />
                {invoiceFormErrors.street && <span className="form-error">{invoiceFormErrors.street}</span>}
              </div>
              <div className="form-row">
                <div className="form-group form-group-small">
                  <label className="form-label">Kod pocztowy</label>
                  <input
                    type="text"
                    className={'form-input' + (invoiceFormErrors.postalCode ? ' form-input-error' : '')}
                    value={invoiceForm.postalCode}
                    onChange={e => setInvoiceForm(prev => ({ ...prev, postalCode: e.target.value }))}
                    placeholder="XX-XXX"
                    maxLength={6}
                  />
                  {invoiceFormErrors.postalCode && <span className="form-error">{invoiceFormErrors.postalCode}</span>}
                </div>
                <div className="form-group form-group-large">
                  <label className="form-label">Miejscowość</label>
                  <input
                    type="text"
                    className={'form-input' + (invoiceFormErrors.city ? ' form-input-error' : '')}
                    value={invoiceForm.city}
                    onChange={e => setInvoiceForm(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="np. Warszawa"
                  />
                  {invoiceFormErrors.city && <span className="form-error">{invoiceFormErrors.city}</span>}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Kraj</label>
                <input
                  type="text"
                  className="form-input"
                  value="Polska"
                  disabled
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={() => setIsInvoiceModalOpen(false)}>Anuluj</button>
              <button className="btn-modal-save" onClick={handleSaveInvoiceData} disabled={isSavingInvoice}>
                {isSavingInvoice ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Payments;
